import React, { useEffect, useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useDispatch } from 'react-redux'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import useModals from 'src/Hooks/useModals'
import { addPowerMeter } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationAction'
import { useTypedSelector } from 'src/State/RootReducer'
import { PowerMeterModalModelResponse } from 'src/Libs/shop/models'
import useShopSystemConfiguration from '../../hooks/use-shop-system-configuration'
import { PowerMeterLimits, PowerMeterQuestionOptions, PowerMeterTabs, PowerMeterTypes, TransformerType, PowerMeterQuestionTypes } from '../../types'
import PowerMeterType from './PowerMeter/PowerMeterType'
import PowerMeterOption from './PowerMeter/PowerMeterOption'

export interface PowerMeterModalProps {
  open: boolean
  onClose: () => void
  place: number
}

const useStyles = makeStyles()(theme => ({
  optionContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(3),
  },
  moreInfoContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
}))

/*
 * This Component Shows a Popup with Couple of Choices before getting into the last current Power Meter that you want to add into the System Configurator
 * This also handle the Logic of Power Meters in the way they should be added into the basket.
 */
const PowerMeterModal: React.FC<PowerMeterModalProps> = ({ open, place, onClose }): JSX.Element => {
  const [productData, setProductData] = useState<PowerMeterModalModelResponse | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const configuratorItems = useTypedSelector(state => state.systemStorage.items)
  const powerMeters = configuratorItems.length > 0 ? configuratorItems[0].configuration.powerMeters : []
  const dispatch = useDispatch()
  const { openShopTransformerModal } = useModals()
  const { getPreSelectedPowerMeter } = useShopSystemConfiguration()
  const { t } = useCustomTranslation()

  const [activeTab, setActiveTab] = useState(PowerMeterTabs.TypesTab)

  const [selectedPowerMeterType, setSelectedPowerMeterType] = useState<PowerMeterQuestionTypes>(PowerMeterQuestionTypes.None)
  const [selectedPowerMeterModel, setSelectedPowerMeterModel] = useState(PowerMeterQuestionOptions.None)
  const [powerMeterSelected, setPowerMeterSelected] = useState('')

  const { classes } = useStyles()

  const externalSourceSize = powerMeters.filter(obj => obj.id <= PowerMeterLimits.ExternalSourceMaxId).length
  const farmingSize = powerMeters.filter(obj => obj.id === PowerMeterLimits.FarmingId).length
  const houseConnectionSize = powerMeters.filter(obj => obj.id === PowerMeterLimits.HouseConnectionId).length

  const powerMeterId = (): string => {
    if (selectedPowerMeterType === PowerMeterQuestionTypes.ExternalSource) {
      const powerMeterNewSize = externalSourceSize + 1
      const powerMeterId =
        powerMeters.length > 0 ? (PowerMeterLimits.IdWithoutNumber + powerMeterNewSize).toString() : PowerMeterLimits.ExternalSourceMinId
      return powerMeterId
    }
    if (selectedPowerMeterType === PowerMeterQuestionTypes.Farming) {
      return PowerMeterLimits.FarmingId
    }
    return PowerMeterLimits.HouseConnectionId
  }

  useEffect(() => {
    setProductData(null)
    if (selectedPowerMeterType !== PowerMeterQuestionTypes.None) {
      setIsLoading(true)
      getPreSelectedPowerMeter(powerMeterId())
        .then(result => setProductData(result))
        .finally(() => setIsLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPowerMeterType])

  const addOtherPowerMeter = (): void => {
    productData &&
      productData.elements.forEach(product => {
        if (product.extensions.e3dcData.customFields.powerMeterType === PowerMeterTypes.Other) {
          dispatch(
            addPowerMeter({
              id: powerMeterId(),
              powerMeterId: product.id,
              powerConverterId: null,
            })
          )
        }
      })
    onClose()
  }

  const TotalTabs = 2

  const handleNext = (): void => {
    const isTabActive = activeTab < TotalTabs
    const isOtherSelectedOnPowerMeterModel = selectedPowerMeterModel === PowerMeterQuestionOptions.Other
    const isPowerModelOtherAndNotNone =
      selectedPowerMeterModel !== PowerMeterQuestionOptions.None && selectedPowerMeterModel !== PowerMeterQuestionOptions.Other

    if (isTabActive && !isOtherSelectedOnPowerMeterModel) {
      setActiveTab(PowerMeterTabs.OptionsTab)
    }

    if (isOtherSelectedOnPowerMeterModel) {
      addOtherPowerMeter()
    }

    if (selectedPowerMeterModel !== PowerMeterQuestionOptions.None && !isOtherSelectedOnPowerMeterModel) {
      onClose()
    }

    if (isPowerModelOtherAndNotNone) {
      openShopTransformerModal({ transformerType: TransformerType.SystemConfigure, id: powerMeterId(), powerMeterId: powerMeterSelected })
    }
  }

  const handlePowerMeterTypeChange = (newPowerMeterType: PowerMeterQuestionTypes): void => {
    setSelectedPowerMeterType(newPowerMeterType)
  }

  const handlePowerMeterOptionChange = (newPowerMeterModel: PowerMeterQuestionOptions, powerMeterId: string): void => {
    setSelectedPowerMeterModel(newPowerMeterModel)
    setPowerMeterSelected(powerMeterId)
  }

  const getOrderValue = (powerMeterType: string): number => {
    switch (powerMeterType) {
      case PowerMeterTypes.WithEthernet:
        return 1
      case PowerMeterTypes.WithoutEthernet:
        return 2
      case PowerMeterTypes.Other:
        return 3
      default:
        return 0
    }
  }

  const modalActions = {
    primary: {
      name: t('shop.common.further'),
      disabled:
        (activeTab === PowerMeterTabs.TypesTab && selectedPowerMeterType === PowerMeterQuestionTypes.None) ||
        (activeTab === PowerMeterTabs.OptionsTab && selectedPowerMeterModel === PowerMeterQuestionOptions.None),
      onClick: () => {
        handleNext()
      },
    },
    secondary: [
      {
        name: t(activeTab === PowerMeterTabs.TypesTab ? 'common.cancel' : 'shop.power-meter.previous-step-label'),
        onClick: () => {
          if (activeTab !== PowerMeterTabs.TypesTab) {
            return setActiveTab(PowerMeterTabs.TypesTab)
          }
          onClose()
        },
      },
    ],
  }

  return (
    <Dialog maxWidth="xs" title={t('shop.power-meter.add-power-meter')} onClose={onClose} place={place} actions={modalActions}>
      <>
        <Box className={classes.moreInfoContainer}>
          <Typography sx={{ my: 1 }} variant="h3" textAlign="left">
            {t('shop.power-meter-type')}
          </Typography>
          <Tooltip placement="left" title={t('shop.power-meter.tooltip')}>
            <IconButton>
              <QuestionMarkIcon color="primary" fontSize="medium" />
            </IconButton>
          </Tooltip>
        </Box>

        {activeTab === PowerMeterTabs.TypesTab && (
          <PowerMeterType
            farmingLength={farmingSize}
            houseConnectionLength={houseConnectionSize}
            externalSourceLength={externalSourceSize}
            selectedQuestion={selectedPowerMeterType}
            onSelectedQuestionChange={handlePowerMeterTypeChange}
          />
        )}
        {activeTab === PowerMeterTabs.OptionsTab && (
          <>
            {!isLoading && (
              <Box className={classes.optionContainer}>
                {productData &&
                  productData.elements
                    .sort(
                      (a, b) =>
                        getOrderValue(a.extensions.e3dcData.customFields.powerMeterType) -
                        getOrderValue(b.extensions.e3dcData.customFields.powerMeterType)
                    )
                    .map(product => {
                      return (
                        <PowerMeterOption
                          key={product.id}
                          productData={product}
                          selectedProduct={selectedPowerMeterModel}
                          onPowerMeterChange={handlePowerMeterOptionChange}
                        />
                      )
                    })}
              </Box>
            )}
          </>
        )}
      </>
    </Dialog>
  )
}

export default PowerMeterModal
